import { LoginAttemptList, type LoginResult, formatDateTime } from '@deizi/shared';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { EmptyState, ErrorBox, Skeleton } from '../../components/States';
import { api } from '../../lib/api';

const RESULT: Record<LoginResult, { label: string; cls: string }> = {
  success: { label: 'Entrou', cls: 'badge ok' },
  bad_password: { label: 'Senha errada', cls: 'badge err' },
  unknown_email: { label: 'E-mail sem conta', cls: 'badge err' },
  inactive: { label: 'Usuário desativado', cls: 'badge err' },
  captcha_failed: { label: 'Captcha recusado', cls: 'badge warn' },
  two_factor_failed: { label: 'Código 2FA errado', cls: 'badge err' },
};

const COLS = '110px 1.6fr 1fr 1.1fr 150px';

/** "06/10 11h40" */
const when = (iso: string) => formatDateTime(iso).replace(/\/\d{4} /, ' ');

/** "Tentativas de acesso" on Configurações · Usuários (Admin; no board, same table patterns). */
export function LoginAttemptsSection() {
  const [failures, setFailures] = useState(false);
  const q = useInfiniteQuery({
    queryKey: ['login-attempts', failures],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: '30' });
      if (failures) p.set('failures', 'true');
      if (pageParam) p.set('cursor', pageParam);
      return api.get(`/login-attempts?${p.toString()}`, LoginAttemptList);
    },
    getNextPageParam: (last) => last.nextCursor,
  });
  const rows = q.data?.pages.flatMap((p) => p.data) ?? [];
  // The bell's "Tentativas de entrada com falha" links to #acessos.
  const { hash } = useLocation();
  const box = useRef<HTMLElement>(null);
  const loaded = q.isSuccess;
  useEffect(() => {
    if (hash === '#acessos' && loaded) box.current?.scrollIntoView?.({ block: 'start' });
  }, [hash, loaded]);

  return (
    <section id="acessos" ref={box} className="card flat" aria-busy={q.isPending}>
      <div className="row between wrap" style={{ padding: '16px 18px 6px', gap: 8 }}>
        <div className="col" style={{ gap: 2 }}>
          <h2>Tentativas de acesso</h2>
          <span className="sm muted">
            Entradas e falhas dos últimos 90 dias, com IP e aparelho.
          </span>
        </div>
        <div className="seg" role="radiogroup" aria-label="Mostrar">
          {(
            [
              [false, 'Todas'],
              [true, 'Só falhas'],
            ] as const
          ).map(([value, label]) => (
            <button
              key={label}
              type="button"
              role="radio"
              aria-checked={failures === value}
              className={failures === value ? 'on' : ''}
              onClick={() => setFailures(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {q.isPending ? <Skeleton rows={3} height={40} /> : null}
      {q.isError ? (
        <ErrorBox
          title="Não foi possível carregar as tentativas"
          onRetry={() => void q.refetch()}
        />
      ) : null}
      {q.isSuccess && rows.length === 0 ? (
        <EmptyState title={failures ? 'Nenhuma falha' : 'Nenhuma entrada ainda'}>
          {failures
            ? 'Senhas erradas, e-mails sem conta e códigos errados aparecem aqui.'
            : 'Cada entrada no sistema aparece aqui.'}
        </EmptyState>
      ) : null}
      {rows.length ? (
        <div className="gt" style={{ overflowX: 'auto' }}>
          <div className="gtr head" style={{ gridTemplateColumns: COLS, minWidth: 680 }}>
            <span>Quando</span>
            <span>E-mail</span>
            <span>IP</span>
            <span>Aparelho</span>
            <span>Resultado</span>
          </div>
          {rows.map((r) => (
            <div key={r.id} className="gtr" style={{ gridTemplateColumns: COLS, minWidth: 680 }}>
              <span className="tab sm">{when(r.at)}</span>
              <span className="sm trunc" title={r.email}>
                {r.userName ? (
                  <>
                    {r.userName} <span className="muted">· {r.email}</span>
                  </>
                ) : (
                  r.email
                )}
              </span>
              <span className="tab sm trunc" title={r.ip}>
                {r.ip}
              </span>
              <span className="sm muted">{r.device}</span>
              <span>
                <span className={RESULT[r.result].cls}>{RESULT[r.result].label}</span>
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {q.hasNextPage ? (
        <div className="row" style={{ padding: '10px 18px 16px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn sm"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            Carregar mais
          </button>
        </div>
      ) : null}
    </section>
  );
}
